'use client';

import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid,  } from 'recharts';

const severityDist = [
  { name: 'Critical', value: 234, color: '#ef4444' },
  { name: 'High', value: 891, color: '#f97316' },
  { name: 'Medium', value: 1432, color: '#f59e0b' },
  { name: 'Low', value: 2187, color: '#3b82f6' },
  { name: 'Info', value: 8934, color: '#52525b' },
];

const weeklyIncidents = [
  { day: 'Mon', new: 8, resolved: 6, breached: 1 },
  { day: 'Tue', new: 12, resolved: 9, breached: 2 },
  { day: 'Wed', new: 6, resolved: 11, breached: 0 },
  { day: 'Thu', new: 15, resolved: 8, breached: 3 },
  { day: 'Fri', new: 9, resolved: 12, breached: 1 },
  { day: 'Sat', new: 3, resolved: 5, breached: 0 },
  { day: 'Sun', new: 4, resolved: 4, breached: 0 },
];

const CustomPieTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 shadow-xl text-xs">
      <p className="font-semibold text-zinc-200">{payload[0].name}</p>
      <p className="text-zinc-400">Count: <span className="font-mono text-zinc-200">{payload[0].value.toLocaleString()}</span></p>
    </div>
  );
};

const CustomBarTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 shadow-xl text-xs">
      <p className="font-mono text-zinc-400 mb-1">{label}</p>
      {payload.map((p: any) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }}></span>
          <span className="text-zinc-400 capitalize">{p.name}:</span>
          <span className="font-semibold text-zinc-200">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function ReportSummaryCharts() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Severity Distribution */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-zinc-200">Event Severity Distribution</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Last 7 days — total {severityDist.reduce((s, d) => s + d.value, 0).toLocaleString('en-US')} events</p>
        </div>
        <div className="flex items-center gap-4">
          <ResponsiveContainer width="50%" height={180}>
            <PieChart>
              <Pie
                data={severityDist}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {severityDist.map((entry, i) => (
                  <Cell key={i} fill={entry.color} fillOpacity={0.85} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex-1 space-y-2">
            {severityDist.map((d) => (
              <div key={d.name} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: d.color }}></span>
                <span className="text-xs text-zinc-500 flex-1">{d.name}</span>
                <span className="font-mono text-xs font-semibold text-zinc-300 tabular-nums">{d.value.toLocaleString('en-US')}</span>
                <span className="text-[11px] text-zinc-600 w-12 text-right tabular-nums">
                  {((d.value / severityDist.reduce((s, x) => s + x.value, 0)) * 100).toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Weekly Incident Trend */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-zinc-200">Weekly Incident Activity</h3>
          <p className="text-xs text-zinc-500 mt-0.5">New vs resolved incidents — this week</p>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={weeklyIncidents} barGap={2} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid stroke="hsl(240 4% 14%)" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#52525b' }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#52525b', fontFamily: 'IBM Plex Mono' }} tickLine={false} axisLine={false} />
            <Tooltip content={<CustomBarTooltip />} />
            <Bar dataKey="new" name="New" fill="#e11d48" fillOpacity={0.8} radius={[2, 2, 0, 0]} barSize={14} />
            <Bar dataKey="resolved" name="Resolved" fill="#10b981" fillOpacity={0.8} radius={[2, 2, 0, 0]} barSize={14} />
            <Bar dataKey="breached" name="SLA Breached" fill="#f59e0b" fillOpacity={0.8} radius={[2, 2, 0, 0]} barSize={14} />
          </BarChart>
        </ResponsiveContainer>
        <div className="flex items-center gap-4 mt-2">
          {[{ label: 'New', color: 'bg-rose-500' }, { label: 'Resolved', color: 'bg-emerald-500' }, { label: 'SLA Breached', color: 'bg-amber-500' }].map((l) => (
            <div key={l.label} className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-sm ${l.color}`}></span>
              <span className="text-[11px] text-zinc-500">{l.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}